import { useContext, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import ModelButton from '../ModelButton';
import { VideoModel } from '../../../../types/store';
import { getVideoCreatorModelsExpanded } from '../../../../selectors/studio/videoCreator';
import { updateVideoCreatorModelsExpanded } from '../../../../actions/studio/videoCreator';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Slider from '@wix/da-ds/pkg/Slider';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowUp';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { URLS } from '@wix/da-url';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';

import s from './ModelSelector.scss';
import sControl from '../VideoCreatorControls.scss';

export interface Props {
  /** When false (tall viewport), all models show and show more/less is hidden. */
  showExpansionControls?: boolean;
}

const NUM_MODELS_CONTRACTED = 4;
const ALL_MODELS: VideoModel[] = [
  VideoModel.HAILUO,
  VideoModel.VEO,
  VideoModel.GROK,
  VideoModel.SEEDANCE,
  VideoModel.WAN,
];

const getVisibleModelButtons = (isMobile: boolean, isModelsExpanded: boolean): VideoModel[] => {
  if (isMobile) return ALL_MODELS;
  return isModelsExpanded ? ALL_MODELS : ALL_MODELS.slice(0, NUM_MODELS_CONTRACTED);
};

const ModelSelector: FC<Props> = ({ showExpansionControls = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useContext(MobileContext);
  const isModelsExpanded = useSelector(getVideoCreatorModelsExpanded);
  const isModelsExpandedEffective = isMobile || !showExpansionControls || isModelsExpanded;
  const visibleModels = getVisibleModelButtons(isMobile, isModelsExpandedEffective);
  const modelButtons = (
    <>
      {visibleModels.map(type => (
        <ModelButton key={type} type={type} />
      ))}
    </>
  );

  return (
    <div
      className={cn(
        s['root'],
        isMobile && s['mobile'],
        !isMobile && !showExpansionControls && s['no-expansion-controls-row']
      )}
      {...(!isMobile && showExpansionControls && { 'aria-expanded': isModelsExpanded })}
    >
      <div className={s['label-row']}>
        <label className={sControl['control-label']}>{t('studio.videoCreator.label.model')}</label>
        <SimpleTooltip
          text={t('studio.videoCreator.modelInfo.tooltip', {
            postProcess: 'reactPostprocessor',
            kbLink: (
              <a
                className={s['tooltip-link']}
                href={URLS.VIDEO_CREATOR_POLICY}
                target="_blank"
                rel="noreferrer"
              >
                {t('studio.videoCreator.modelInfo.tooltip.link')}
              </a>
            ),
          })}
          listenOnClicks={isMobile}
          usePortal
        >
          <IconButton
            size="small"
            icon={InfoIcon}
            aria-label={t('studio.videoCreator.aria.modelInfo')}
            className={s['info-button']}
          />
        </SimpleTooltip>
      </div>
      {isMobile ? (
        <Slider className={s['slider']}>
          {modelButtons}

          {/* UX said the slider has to go all the way to the edge of the screen, but it looks
              weird for it to stop before it is in line with the other elements that are 16px
              from the edge, so a spacer is needed to make it look aligned.*/}
          <div className={s['slider-spacer']} />
        </Slider>
      ) : (
        <>
          <div className={s['content']}>
            {modelButtons}
            {!isMobile && isModelsExpandedEffective && (
              <div className={s['model-button-placeholder']}>
                {t('studio.videoCreator.models.comingSoon')}
              </div>
            )}
          </div>
          {!isMobile && showExpansionControls && (
            <TextButton
              className={s['show-more']}
              size="small"
              variant="main"
              onClick={() => dispatch(updateVideoCreatorModelsExpanded(!isModelsExpanded))}
            >
              {isModelsExpanded ? (
                <>
                  {t('studio.videoCreator.models.showLess')}
                  <ArrowUpIcon className={s['show-more-chevron']} size={IconSize.SMALLER} />
                </>
              ) : (
                <>
                  {t('studio.videoCreator.models.showMore')}
                  <ArrowDownIcon className={s['show-more-chevron']} size={IconSize.SMALLER} />
                </>
              )}
            </TextButton>
          )}
        </>
      )}
    </div>
  );
};

ModelSelector.displayName = 'ModelSelector';
export default ModelSelector;
